import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { CampaignSettingsAbstractComponent } from '../campaign-settings-abstract.component';
import {
  BusinessHours,
  CallCenterAction,
  CallCenterHours,
  Campaign,
  CampaignSection,
  ContactType,
  ContactTypeLabels,
  DayEndTime,
  DayEndTimeLabel,
  DayStartTime,
  DefaultCallCenterCloseTime,
  DefaultCallCenterOpenTime,
  LifelinePhoneSetting,
  LifelinePhoneSettingOptions,
  NotificationContact,
  PhoneContact,
  SipRegion,
  SipRegionLabels,
  User,
  Weekdays,
} from '../../_models';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  AlertService,
  CampaignService,
  LoaderService,
  SideNavService,
  UserService,
  CallCenterActionService,
} from '../../_services';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { PhoneContactFieldsComponent } from './phone-contact-fields.component';
import { takeUntil } from 'rxjs/operators';
import { expandCollapse } from '../../_shared/animations/app-animations';

@Component({
  templateUrl: './campaign-call-center-settings.component.html',
  styleUrls: ['../campaign-section.scss', './campaign-call-center-settings.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [expandCollapse],
})
export class CampaignCallCenterSettingsComponent extends CampaignSettingsAbstractComponent {
  public section = CampaignSection.CallCenter;
  public ContactType = ContactType;
  public phoneContacts: PhoneContact[] = [];
  public selectedPhoneContact: PhoneContact = null;
  @ViewChild(PhoneContactFieldsComponent, { static: false }) phoneContactFields: PhoneContactFieldsComponent = null;
  public phoneVisibilityRulesOpen: boolean = false;
  public lifelinePhoneSettings = LifelinePhoneSettingOptions;
  public notificationContactTypes = [];
  public sipEnabled: boolean = false;
  public handOffEnabled: boolean = false;
  public sipRegions: { value: number; label: string }[] = [];
  public showPassword: boolean = false;
  user: User;
  timeOptions: { value: string; label: string }[] = [];
  actions: CallCenterAction[] = [];
  actionFields = [
    { name: 'return_action', label: 'RMA Return' },
    { name: 'exchange_action', label: 'RMA Exchange' },
    { name: 'cancel_action', label: 'Cancellation (Hold Status)' },
    { name: 'cancel_subscription_action', label: 'Cancel Subscription' },
  ];

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected campaignService: CampaignService,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    protected sideNav: SideNavService,
    protected modalService: NgxSmartModalService,
    protected loaderService: LoaderService,
    protected userService: UserService,
    protected actionService: CallCenterActionService
  ) {
    super(router, location, route, campaignService, sideNav, alertService, loaderService);
    this.generateTimeOptions();
  }

  ngOnInit() {
    this.userService
      .getCurrent()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (user: User) => {
          this.user = user;
        },
        (error) => {
          this.handleError(error);
        }
      );

    this.actionService
      .list()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (actions: CallCenterAction[]) => {
          this.actions = actions;
        },
        (error) => {
          this.handleError(error);
        }
      );

    this.form = this.formBuilder.group({
      support_phone: [null, null],
      support_email: [null, [Validators.email]],
      phone_contacts: this.formBuilder.array([]),
      lifeline_phone_setting: [null, null],
      show_phone_link: [null, null],
      call_center_hours: this.formBuilder.array([]),
      notification_contacts: this.formBuilder.array([]),
      return_action: [null],
      exchange_action: [null],
      cancel_action: [null],
      cancel_subscription_action: [null],
      sip_domain: this.formBuilder.group({
        outbound_uri: [null],
        outbound_username: [null],
        outbound_password: [null],
        outbound_secure: [false],
        outbound_refer: [false],
        region: [SipRegion.Default],
      }),
    });

    const hoursArray = this.form.get('call_center_hours') as FormArray;

    Weekdays.forEach((day: string) => {
      hoursArray.push(
        this.formBuilder.group(
          {
            label: [day, [Validators.required]],
            isOpen: [false, [Validators.required]],
            openTime: [DefaultCallCenterOpenTime, [Validators.required]],
            closeTime: [DefaultCallCenterCloseTime, [Validators.required]],
          },
          { validators: this.callCenterFieldValidator.bind(this) }
        )
      );
    });

    ContactTypeLabels.forEach((value, index) => {
      if (index > 0) {
        this.notificationContactTypes.push({ id: index, label: value });
      }
    });

    SipRegionLabels.forEach((label, value) => {
      this.sipRegions.push({ value: value, label: label });
    });

    super.ngOnInit();
  }

  onCampaignFetch(data: Campaign) {
    this.phoneContacts = [];

    if (data.support_phone) {
      this.phoneContacts.push({
        id: null,
        phone: data.support_phone,
        contact_type: ContactType.Support,
      } as PhoneContact);
    }

    const phoneContactsArray = this.form.get('phone_contacts') as FormArray;
    data.phone_contacts.forEach((contact: PhoneContact) => {
      this.phoneContacts.push(contact);
      phoneContactsArray.push(
        this.formBuilder.group({
          id: [contact.id, null],
          contact_type: [contact.contact_type, [Validators.required]],
          phone: [contact.phone, [Validators.required]],
        })
      );
    });

    data.call_center_hours.forEach((hours: CallCenterHours) => {
      this.changeCallCenterHours(Weekdays[hours.dow], true, hours.starttime, hours.endtime, hours.dow);
    });

    this.disableClosedHours();

    data.notification_contacts.forEach((contact: NotificationContact) => {
      this.addNotificationContact(contact.contact_type, contact.phone_sms, contact.email);
    });

    this.sipEnabled = !!(data.sip_domain && data.sip_domain.outbound_uri);

    // Handle null/undefined values to prevent patchValue errors
    this.form.patchValue({
      support_phone: data.support_phone || null,
      lifeline_phone_setting:
        data.lifeline_phone_setting || this.lifelinePhoneSettings[LifelinePhoneSetting.Never].value,
      show_phone_link: data.show_phone_link || null,
      support_email: data.support_email || null,
      sip_domain: data.sip_domain || {
        outbound_uri: null,
        outbound_username: null,
        outbound_password: null,
        outbound_secure: false,
        outbound_refer: false,
        region: SipRegion.Default,
      },
    });
  }

  disableClosedHours() {
    const hoursArray = this.form.get('call_center_hours') as FormArray;

    hoursArray.controls.forEach((group: FormGroup) => {
      if (!group.get('isOpen').value) {
        group.get('openTime').disable({ emitEvent: false });
        group.get('closeTime').disable({ emitEvent: false });
      } else {
        group.get('openTime').enable({ emitEvent: false });
        group.get('closeTime').enable({ emitEvent: false });
      }
    });
  }

  isCallCenterOpenEveryday(): boolean {
    const hoursArray = this.form.get('call_center_hours') as FormArray;
    return hoursArray.controls.every(
      (group: FormGroup) =>
        group.get('isOpen').value &&
        group.get('openTime').value === DayStartTime &&
        group.get('closeTime').value === DayEndTime
    );
  }

  onCallCenterEverydayChange(event) {
    const isChecked = event.target.checked;
    const hoursArray = this.form.get('call_center_hours') as FormArray;
    hoursArray.controls.forEach((group: FormGroup) => {
      if (!isChecked) {
        group.get('openTime').patchValue(DefaultCallCenterOpenTime);
        group.get('closeTime').patchValue(DefaultCallCenterCloseTime);
      } else {
        group.get('openTime').patchValue(DayStartTime);
        group.get('closeTime').patchValue(DayEndTime);
      }
      group.get('isOpen').patchValue(isChecked);
    });
    this.disableClosedHours();
  }

  private generateTimeOptions(): void {
    this.timeOptions = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const value = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`;
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const displayMinute = minute.toString().padStart(2, '0');
        const period = hour < 12 ? 'AM' : 'PM';
        const label = `${displayHour}:${displayMinute} ${period}`;

        this.timeOptions.push({ value, label });
      }
    }
    this.timeOptions.push({ value: DayEndTime, label: DayEndTimeLabel });
  }

  formatPhone(value: string): string {
    const raw = value.replace(/^\+1/, '');
    const areaCode = raw.slice(0, 3);
    const prefix = raw.slice(3, 6);
    const lineNumber = raw.slice(6, 10);
    return `${areaCode}-${prefix}-${lineNumber}`;
  }

  getContactTypeLabel(contact_type: ContactType) {
    return ContactTypeLabels[contact_type] || '';
  }

  deletePhoneContact(contact: PhoneContact) {
    if (contact.contact_type === ContactType.Support) {
      this.form.patchValue({ support_phone: null });
    } else {
      const phoneContactsArray = this.form.get('phone_contacts') as FormArray;
      const index = phoneContactsArray.controls.findIndex(
        (c) => (contact.id && contact.id === c.value.id) || c.value.contact_type === contact.contact_type
      );
      if (index > -1) {
        phoneContactsArray.removeAt(index);
      }
    }
    this.phoneContacts = this.phoneContacts.filter(
      (item) => (contact.id && contact.id !== item.id) || item.contact_type !== contact.contact_type
    );
  }

  openPhoneContactDialog(phoneContact: PhoneContact = null) {
    this.selectedPhoneContact = {
      id: phoneContact ? phoneContact.id : null,
      contact_type: phoneContact ? phoneContact.contact_type : null,
      phone: phoneContact ? phoneContact.phone : null,
    } as PhoneContact;

    let _this = this;
    setTimeout(() => {
      _this.modalService.getModal('phoneContactDialog').open();
    });
  }

  closePhoneContactDialog() {
    this.selectedPhoneContact = null;
    this.modalService.getModal('phoneContactDialog').close();
  }

  onSavePhoneContact() {
    if (this.phoneContactFields) {
      const value: PhoneContact = this.phoneContactFields.getFormData();

      if (value) {
        if (value.contact_type === ContactType.Support) {
          this.form.patchValue({ support_phone: value.phone });
        } else {
          const phoneContactsArray = this.form.get('phone_contacts') as FormArray;
          const index = phoneContactsArray.controls.findIndex(
            (c) => (value.id && value.id === c.value.id) || c.value.contact_type === value.contact_type
          );
          if (index > -1) {
            phoneContactsArray.controls[index].patchValue(value);
          } else {
            phoneContactsArray.push(
              this.formBuilder.group({
                id: [value.id, null],
                contact_type: [value.contact_type, [Validators.required]],
                phone: [value.phone, [Validators.required]],
              })
            );
          }
        }
        const index = this.phoneContacts.findIndex(
          (item) => (value.id && value.id === item.id) || item.contact_type === value.contact_type
        );
        if (index > -1) {
          this.phoneContacts[index] = value;
        } else {
          this.phoneContacts.push(value);
        }
        this.closePhoneContactDialog();
      }
    }
  }

  isSupportPhoneAdded() {
    return !!this.form.value.support_phone;
  }

  protected getFormData(): any {
    let formData = super.getFormData();
    formData.phone_contacts = (formData.phone_contacts || []).map((contact) => {
      const newContact = { ...contact };
      if (newContact.id === null || newContact.id === undefined) {
        delete newContact.id;
      }
      return newContact;
    });

    let callCenterHours = [];
    if (formData.call_center_hours && formData.call_center_hours.length) {
      formData.call_center_hours.forEach((day: BusinessHours, dow: number) => {
        if (day.isOpen) {
          if (day.closeTime === '00:00:00') {
            day.closeTime = '23:59:59'; // convert midnight end time to the last second of the day
          }

          callCenterHours.push({
            dow: dow,
            starttime: day.openTime,
            endtime: day.closeTime,
          });
        }
      });
    }

    let sipDomain = formData.sip_domain;
    if (!this.sipEnabled) {
      sipDomain = null;
    }

    formData.sip_domain = sipDomain;
    formData.call_center_hours = callCenterHours;
    return formData;
  }

  getPhoneTypeOptions() {
    let typesAlreadyAdded = [];
    let formValue = this.form.value;

    if (this.isSupportPhoneAdded()) {
      typesAlreadyAdded.push(ContactType.Support);
    }

    typesAlreadyAdded = typesAlreadyAdded.concat(
      (formValue.phone_contacts || []).map((contact: PhoneContact) => contact.contact_type)
    );
    return ContactTypeLabels.filter((label, index) => {
      return typesAlreadyAdded.indexOf(index) < 0 || index === this.selectedPhoneContact.contact_type;
    }).map((label) => {
      return { id: ContactTypeLabels.indexOf(label), text: label };
    });
  }

  private callCenterFieldValidator(group: FormGroup) {
    if (group.touched) {
      let controls = group.controls;
      let openTimeValue = controls.openTime.value;
      let closeTimeValue = controls.closeTime.value;
      openTimeValue = openTimeValue;
      closeTimeValue = closeTimeValue;
      openTimeValue = openTimeValue.split(':').map((value) => parseInt(value, 10));
      closeTimeValue = closeTimeValue.split(':').map((value) => parseInt(value, 10));

      // If open time hour is greater than close time hour
      if (openTimeValue[0] > closeTimeValue[0]) {
        group.controls.closeTime.setErrors({ invalid: 'Close time should be greater than open time.' });
      } else if (openTimeValue[0] === closeTimeValue[0] && openTimeValue[1] >= closeTimeValue[1]) {
        // if open time hours are equal to close time hours but open time minutes are greater than close time minutes
        group.controls.closeTime.setErrors({ invalid: 'Close time should be greater than open time.' });
      } else {
        group.controls.closeTime.setErrors(null);
      }
    }
  }

  changeCallCenterHours(
    label: string = null,
    isOpen: boolean = false,
    openTime: string = null,
    closeTime: string = null,
    dow: number = 0
  ) {
    let fields = this.form.get('call_center_hours') as FormArray;
    let value = {
      label: label,
      isOpen: isOpen,
      openTime: openTime,
      closeTime: closeTime,
    } as BusinessHours;

    if (value.closeTime === '23:59:00') {
      value.closeTime = '23:59:59';
    }

    fields.controls[dow].patchValue(value);
  }

  get callCenterHours() {
    return this.form.get('call_center_hours') as FormArray;
  }

  isCallCenterFormFieldOpen(index): boolean {
    return this.callCenterHours.at(index).get('isOpen').value;
  }

  changeCallCenterFormField(event, index) {
    const control = this.callCenterHours.at(index).get('isOpen');
    control.patchValue(event.target.checked);

    this.disableClosedHours();
  }

  getCallCenterDay(index): string {
    return this.callCenterHours.at(index).get('label').value;
  }

  getCallCenterFieldErrors(index, fieldName) {
    const control = this.callCenterHours.at(index).get(fieldName);
    let errors = control.errors;

    if (errors) {
      if ('invalid' in errors) {
        return errors['invalid'];
      }
    }

    return '';
  }

  setCallCenterTime(index, timeStamp, fieldName) {
    const control = this.callCenterHours.at(index).get(fieldName);
    control.patchValue(timeStamp);
  }

  get notificationContacts() {
    return this.form.get('notification_contacts') as FormArray;
  }

  addNotificationContact(type: ContactType = null, phone: string = null, email: string = null) {
    this.notificationContacts.push(
      this.formBuilder.group({
        contact_type: [type, null],
        phone_sms: [phone, null],
        email: [email, [Validators.email]],
      })
    );
  }

  removeNotificationContact(index) {
    this.notificationContacts.removeAt(index);
  }

}
